import seedrandom from "seedrandom"

export function getSeedRandomColor(seed: string | number) {
  const random = seedrandom(seed as string)
  // Math.pow(15, 6) instead of Math.pow(16, 6)
  // attempting to avoid white colors which are the highest values
  let color = `#${Math.floor(Number(random()) * Math.pow(15, 6)).toString(16)}` // magic
  // A few colors are invalid with only 5 characters instead of 6
  // This is the case when using Math.pow(16, 6) as well
  return `${color}666`.slice(0, 7)
}

// Returns a color scheme from green to red based on how far in the past the date is (0-12 months)
export function relativeTimeColorScheme(date: Date | string | number): string {
  const now = new Date()
  const inputDate =
    typeof date === "string" || typeof date === "number" ? new Date(date) : date
  // Calculate months difference
  const monthsAgo =
    (now.getFullYear() - inputDate.getFullYear()) * 12 +
    (now.getMonth() - inputDate.getMonth())

  if (inputDate.getTime() > now.getTime()) {
    // Future date: green
    return "relativeTime0"
  }
  // Clamp to 12 months max
  const clamped = Math.max(1, Math.min(monthsAgo, 12))
  return `relativeTime${clamped}`
}

export type UnderscoredColorPalette = Record<`_${number}`, string | null> & {
  __typename?: string
}

// export type ColorPalette = Record<number, { value: string | null }>
export type ColorPalette = Record<string, { value: string }>

// Converts a color palette object from the GQL API to a Chakra color palette.
export function getPrimaryPalette(
  colorPalette: UnderscoredColorPalette | ColorPalette
): ColorPalette {
  let palette = { ...colorPalette }
  delete palette.__typename
  // Remove the preceeding "_" from keys.
  palette = Object.entries(palette).reduce((acc, [key, value]) => {
    // The keys may not be preceded by an undescore if the input is already a ColorPalette, so we take the last part after splitting by "_".
    const newKey = key.split("_").slice(-1)[0]
    if (value && newKey) {
      acc[newKey] = { value }
    }
    return acc
  }, {} as ColorPalette)

  return palette
}

type ColorPaletteSemanticTokensOptions = {
  // What does the color palette's "main" color value map to?
  primaryValue?: string
} & (
  | {
      contrastValue?: string
      contrastColor?: never
    }
  | {
      contrastColor?: string
      contrastValue?: never
    }
)

// Generate semantic tokens for a given color palette name.
export function getColorPaletteSemanticTokens(
  paletteName: string,
  options?: ColorPaletteSemanticTokensOptions
) {
  const primaryValue = options?.primaryValue ?? "600"
  const contrastValue =
    options && "contrastValue" in options ? options.contrastValue : undefined
  const contrastColor =
    options && "contrastColor" in options ? options.contrastColor : undefined

  const primaryColorToken = `{colors.${paletteName}.${primaryValue}}`
  const contrastColorToken =
    contrastColor || `{colors.${paletteName}.${contrastValue ?? "50"}}`

  return {
    [paletteName]: {
      contrast: {
        value: {
          _light: contrastColorToken,
          _dark: primaryColorToken
        }
      },
      fg: {
        value: {
          _light: `{colors.${paletteName}.700}`,
          _dark: `{colors.${paletteName}.300}`
        }
      },
      subtle: {
        value: {
          _light: `{colors.${paletteName}.100}`,
          _dark: `{colors.${paletteName}.900}`
        }
      },
      muted: {
        value: {
          _light: `{colors.${paletteName}.200}`,
          _dark: `{colors.${paletteName}.800}`
        }
      },
      emphasized: {
        value: {
          _light: `{colors.${paletteName}.300}`,
          _dark: `{colors.${paletteName}.700}`
        }
      },
      solid: {
        value: {
          _light: primaryColorToken,
          _dark: contrastColorToken
        }
      },
      focusRing: {
        value: {
          _light: primaryColorToken,
          _dark: contrastColorToken
        }
      },
      border: {
        value: {
          _light: primaryColorToken,
          _dark: `{colors.${paletteName}.400}`
        }
      }
    }
  }
}
